import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Github, Mail, Twitter } from "lucide-react";
import PortalLayout from "@/components/portal/PortalLayout";
import { supabase } from '@/integrations/supabase/client';
import { supabaseGet, supabasePatch } from '@/utils/supabaseRest';
import { useToast } from '@/components/ui/use-toast';

export const PortalProfile = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError) throw authError;
        if (!user) throw new Error('User not authenticated.');

        setEmail(user.email || '');

        const rows = await supabaseGet<{ name: string }[]>(`profiles?select=name&id=eq.${user.id}`);
        if (rows.length > 0) {
          setName(rows[0].name || '');
        }
      } catch (err: any) {
        setError(err.message);
        toast({
          title: 'Error loading profile',
          description: err.message,
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [toast]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpdating(true);
    setError(null);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('User not authenticated.');

      // Update email in auth.users if it has changed
      if (email !== user.email) {
        const { error: emailUpdateError } = await supabase.auth.updateUser({ email });
        if (emailUpdateError) throw emailUpdateError;
      }

      // Update name in user_profiles
      await supabasePatch(`profiles?id=eq.${user.id}`, { name });

      toast({
        title: 'Profile Updated',
        description: 'Your profile has been successfully updated.',
      });
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error updating profile',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast({
        title: 'Password Mismatch',
        description: 'New password and confirm password do not match.',
        variant: 'destructive',
      });
      return;
    }
    if (!newPassword) {
      toast({
        title: 'Password Required',
        description: 'New password cannot be empty.',
        variant: 'destructive',
      });
      return;
    }

    setChangingPassword(true);
    setError(null);
    try {
      const { error: passwordUpdateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (passwordUpdateError) throw passwordUpdateError;

      toast({
        title: 'Password Updated',
        description: 'Your password has been successfully updated.',
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error updating password',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <PortalLayout>
      <div className="container mx-auto py-6 space-y-6 max-w-4xl">
        <div>
          <h1 className="text-3xl font-bold">Profile Settings</h1>
          <p className="text-gray-600 mt-2">
            Manage your account settings and social connections
          </p>
        </div>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Account Information</h2>
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading || updating}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Your Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading || updating}
              />
            </div>
            <Button type="submit" disabled={loading || updating}>
              {updating ? 'Updating...' : 'Update Profile'}
            </Button>
          </form>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Security</h2>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                placeholder="Enter current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={changingPassword}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={changingPassword}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={changingPassword}
              />
            </div>
            <Button type="submit" disabled={changingPassword}>
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </Card>

        {/* Commenting out Connected Accounts section
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Connected Accounts</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Github className="h-5 w-5" />
                <div>
                  <h3 className="font-medium">GitHub</h3>
                  <p className="text-sm text-gray-600">Connect your GitHub account</p>
                </div>
              </div>
              <Button variant="outline">Connect</Button>
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Twitter className="h-5 w-5 text-blue-400" />
                <div>
                  <h3 className="font-medium">Twitter</h3>
                  <p className="text-sm text-gray-600">Connect your Twitter account</p>
                </div>
              </div>
              <Button variant="outline">Connect</Button>
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-red-500" />
                <div>
                  <h3 className="font-medium">Google</h3>
                  <p className="text-sm text-gray-600">Connect your Google account</p>
                </div>
              </div>
              <Button variant="outline">Connect</Button>
            </div>
          </div>
        </Card>
        */}
      </div>
    </PortalLayout>
  );
};

export default PortalProfile;
